import {Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {RouterLink} from "@angular/router";
import {TranslatePipe} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {IconComponent} from "../../libs/icon/icon.component";
import {ModalComponent} from "../../libs/modal/modal.component";
import {ModalConfig} from "../../libs/modal/modal.config";
import {NoticeDto} from "../../repositories/dto/notice.dto";
import {NoticeService} from "../../services/notice.service";
import {NotificationService} from "../../services/notification/notification.service";
import {StateService} from "../../services/state/state.service";
import {isDefined, notDefined, unsubscribe} from "../../libs/utils/epic.utils";
import {Paths} from "../../paths";

/**
 * Die offene Pflichtmitteilung über allen Seiten.
 *
 * ## 🔑 Eine Mitteilung, zwei Ansichten
 *
 * Wer den Scope `AGREEMENT` hat, bekommt die Aufforderung zu unterzeichnen. Wer ihn nicht hat, bekommt die
 * **Hinweisansicht** mit den zeichnungsberechtigten Personen — namentlich, mit einem Weg, sie zu erreichen.
 *
 * ⛔ **Ohne diesen Ausweg ist der Hinweis keine Aufforderung, sondern eine Belästigung** (Abschnitt 2.1 des
 * Zustimmungsplans): Er richtet sich an jemanden, der nichts tun kann.
 *
 * ⚠️ **Kein Route-Guard, keine Sperre.** Der AVV-Fall sperrt niemanden (Entscheidung vom 2026-08-27); die
 * Eskalation läuft außerhalb des Dashboards. Dieses Banner ist ein Hinweis, sonst nichts.
 */
@Component({
  selector: 'acc-notice-banner',
  imports: [
    TranslatePipe,
    IconComponent,
    ModalComponent,
    FormsModule,
    RouterLink
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './notice-banner.component.html'
})
export class NoticeBannerComponent implements OnInit, OnDestroy {

  @ViewChild('objectionModal')
  public objectionModal!: ModalComponent;

  protected readonly agreementPath: string = '/' + Paths.AGREEMENT;

  /**
   * ⛔ **Der Dialog schließt sich nicht von selbst.** Ein Widerspruch ohne Begründung ist später nicht belegbar; ein
   * automatisch schließender Dialog verwürfe eine leere Eingabe wortlos, und der Widersprechende hielte seine Erklärung
   * für abgegeben.
   */
  protected readonly objectionConfig = new ModalConfig({
    autoClose: false,
    inputConfirmMsgCode: 'notice.action.object',
    inputCancelMsgCode: 'modal.input.cancel'
  });

  protected objectionReason = '';

  protected notice: NoticeDto | undefined;

  /** Gegen den Doppelklick; der harte Riegel sitzt im Backend. */
  protected reminding: boolean = false;

  private readonly noticeService: NoticeService = inject(NoticeService);

  private readonly stateService: StateService = inject(StateService);

  private readonly notificationService: NotificationService = inject(NotificationService);

  private readonly subscriptions: Subscription[] = [];

  public ngOnInit(): void {
    this.subscriptions.push(this.noticeService.changed().subscribe((n) => this.notice = n));
    // Die Mitteilung haengt an der Organisation - beim Wechsel wird neu gefragt, nicht die alte weitergezeigt.
    this.subscriptions.push(this.stateService.getOrgDistinct().subscribe(() => this.load()));
  }

  public ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  /** „Später" — ein Aufschub, keine Antwort. Es entsteht keine Zeile in `notice_ack`. */
  protected dismiss(): void {
    if (isDefined(this.notice)) {
      this.noticeService.dismiss(this.notice as NoticeDto);
    }
  }

  /**
   * „Zur Kenntnis genommen" — bei `INFO`, und zwar **für jeden Nutzer**.
   *
   * ⛔ **Nicht an das Zeichnungsrecht koppeln.** Der Endpunkt verlangt dafür nur `ANY:R`: Eine Mitteilung zur Kenntnis
   * zu nehmen ist etwas anderes, als einen Vertrag zu schließen. Wer beides verwechselt, sperrt die Mitteilung nach
   * § 7 Abs. 4 für genau die Leute aus, an die sie sich richtet.
   */
  protected acknowledge(): void {
    if (isDefined(this.notice)) {
      this.subscriptions.push(this.noticeService.acknowledge(this.notice as NoticeDto).subscribe());
    }
  }

  /**
   * Widerspruch nach § 7 Abs. 5 — **eine Erklärung mit Rechtsfolge**, deshalb mit Begründung.
   *
   * ⚠️ **Über das Dialogmuster des Hauses, nicht über `window.prompt`.** Der Browserdialog war am 2026-09-10 die
   * einzige Stelle dieser Art in der ganzen Anwendung: unübersetzt, unformatiert, ohne Längenbegrenzung — und in
   * manchen Browsern unterdrückbar. Für eine Erklärung, an der eine Rechtsfolge hängt, ist das der falsche Weg.
   */
  protected object(): void {
    if (isDefined(this.notice)) {
      this.objectionReason = '';
      this.objectionModal.open().then();
    }
  }

  /** ⛔ Ohne Begründung wird nichts abgeschickt — der Dialog bleibt stehen und zeigt, was fehlt. */
  protected confirmObjection(): void {
    if (notDefined(this.notice) || this.objectionReason.trim().length === 0) {
      return;
    }
    this.subscriptions.push(this.noticeService.acknowledge(this.notice as NoticeDto, true, this.objectionReason.trim()).subscribe());
    this.objectionModal.close();
  }

  protected hasNoReason(): boolean {
    return this.objectionReason.trim().length === 0;
  }

  protected isConsent(): boolean {
    return this.notice?.type === 'CONSENT';
  }

  /**
   * „Administrator erinnern" — eine Systemmail an die Zeichnungsberechtigten, verschickt von `core`.
   *
   * ⚠️ **„Heute schon erinnert" ist kein Fehler**, sondern die Drosselung — als Fehler gemeldet, hielte der Bittende
   * die Schaltfläche für kaputt.
   */
  protected remind(): void {
    if (notDefined(this.notice) || this.reminding) {
      return;
    }
    this.reminding = true;
    this.subscriptions.push(this.noticeService.remind(this.notice as NoticeDto).subscribe({
      next: (result) => {
        this.reminding = false;
        this.notificationService.addSuccessMessage(result?.sent === true ? 'notice.remind.sent' : 'notice.remind.already');
      },
      error: () => {
        this.reminding = false;
        this.notificationService.addErrorMessage('notice.remind.error');
      }
    }));
  }

  protected hasSignatories(): boolean {
    return (this.notice?.signatories?.length ?? 0) > 0;
  }

  private load(): void {
    this.subscriptions.push(this.noticeService.load().subscribe());
  }
}
